import React, { useCallback } from "react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import {
  BooleanIndicator,
  ContentSection,
  DisplayValue,
  RecordDetailCard,
  RecordFieldRow,
  StatusPill,
  SudsPill,
  ValuePillList,
} from "../components/DataDisplay";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import { ClientRankLevelRulesSection } from "../components/ClientRankLevelRulesSection";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { ClientPrimaryTabs } from "../components/PortalNavigation";
import { PortalLink } from "../components/PortalLink";
import { editClientPath } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import { getClinicianPortalClientSnapshot } from "../api/portalRequestCache";
import {
  clientWorkspaceBreadcrumbs,
  useClientWorkspace,
} from "../components/ClientWorkspace";

export function ClientProfilePage({
  clinicianCode,
  patientId,
}: {
  clinicianCode: string;
  patientId: string;
}) {
  const workspace = useClientWorkspace();
  const request = useCallback(
    (signal: AbortSignal) =>
      workspace.accessScope === "team"
        ? clinicianPortalApi.getClient(
            clinicianCode,
            patientId,
            false,
            signal,
            "team",
          )
        : clinicianPortalApi.getClient(clinicianCode, patientId, false, signal),
    [clinicianCode, patientId, workspace.accessScope],
  );
  const relationOptionsRequest = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getRelationOptions(clinicianCode, signal),
    [clinicianCode],
  );
  const state = usePortalData(request);
  const relationOptionsState = usePortalData(relationOptionsRequest, {
    enabled: !workspace.readOnly,
  });
  const snapshot = getClinicianPortalClientSnapshot(patientId, workspace.accessScope);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found && data.authorized && data.therapist && data.patient,
  );
  const patient = available
    ? data?.patient
    : state.status === "loading"
      ? snapshot?.patient
      : null;
  const therapist = available
    ? data?.therapist
    : state.status === "loading"
      ? snapshot?.therapist
      : null;
  const relationOptions =
    relationOptionsState.status === "success" ||
    relationOptionsState.status === "refreshing"
      ? relationOptionsState.data
      : undefined;
  const clinicName =
    patient?.clinicId &&
    relationOptions?.found &&
    relationOptions.authorized
      ? relationOptions.options.find(
          (option) => option.value === patient.clinicId,
        )?.name
      : undefined;
  const clinicDisplayValue = patient?.clinicId
    ? clinicName ||
      (relationOptionsState.status === "loading"
        ? "Loading clinic…"
        : "Clinic unavailable")
    : "";

  return (
    <ClinicianPortalLayout
      activeGlobalKey={workspace.source === "team" ? "team" : "clients"}
      clinicianCode={clinicianCode}
      therapistRole={therapist?.role}
    >
      <PortalHeader
        density="compact"
        title={
          patient
            ? `Client Record: ${patient.accessCode} • ${patient.nickname}`
            : "Client Record"
        }
        clinicianName={therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={clientWorkspaceBreadcrumbs(
              workspace,
              patientId,
              patient?.nickname || "Client Profile",
            )}
          />
        }
      >
        <ClientPrimaryTabs
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="profile"
        />
      </PortalHeader>
      <div className="cp-page-content cp-page-content--profile cp-page-content--compact-client">
        {state.status === "loading" && !snapshot ? (
          <LoadingState label="Loading client profile" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Client profile is not available")} />
        ) : null}
        {patient && (available || state.status === "loading") ? (
          <>
            <ContentSection
              title="Client Profile"
              hideTitle
              actions={workspace.canEditClient ? (
                <PortalLink
                  className="cp-button cp-button--primary"
                  path={editClientPath(patientId)}
                  clinicianCode={clinicianCode}
                >
                  Edit Client
                </PortalLink>
              ) : undefined}
            >
              <p className="cp-content-section__description">
                Core client information, clinical context, assistant settings,
                and preferences.
              </p>
              <RecordDetailCard>
                <RecordFieldRow label="Nickname">
                  <DisplayValue value={patient.nickname} />
                </RecordFieldRow>
                <RecordFieldRow label="Access Code">
                  <DisplayValue value={patient.accessCode} />
                </RecordFieldRow>
                <RecordFieldRow label="Clinic">
                  <DisplayValue value={clinicDisplayValue} />
                </RecordFieldRow>
                <RecordFieldRow label="Status">
                  <StatusPill status={patient.status} />
                </RecordFieldRow>
                <RecordFieldRow label="Clinical Summary">
                  <DisplayValue value={patient.clinicalSummary} />
                </RecordFieldRow>
                <RecordFieldRow label="High SUDS Threshold">
                  <SudsPill value={patient.highSudsThreshold} />
                </RecordFieldRow>
                <RecordFieldRow label="SUDS Entry Style">
                  <DisplayValue value={patient.sudsInputStyle} />
                </RecordFieldRow>
                <RecordFieldRow label="Self-initiated">
                  <BooleanIndicator value={patient.selfInitiatedPermitted} />
                </RecordFieldRow>
                <RecordFieldRow label="Coping Strategies">
                  <ValuePillList
                    values={patient.copingStrategies}
                    kind="coping"
                  />
                </RecordFieldRow>
                <RecordFieldRow label="Fun Activities">
                  <ValuePillList values={patient.funActivities} kind="fun" />
                </RecordFieldRow>
              </RecordDetailCard>
            </ContentSection>
            <ContentSection title="Progress & Rewards">
              <p className="cp-content-section__description">
                Review the client’s points, coins, current rank, and earned
                badges.
              </p>
              <RecordDetailCard>
                <RecordFieldRow label="Points Earned">
                  <DisplayValue value={patient.pointsEarned} />
                </RecordFieldRow>
                <RecordFieldRow label="Coins Earned">
                  <DisplayValue value={patient.coins} />
                </RecordFieldRow>
                <RecordFieldRow label="Rank Level">
                  <DisplayValue value={patient.badgeLevel} />
                </RecordFieldRow>
                <RecordFieldRow label="Badge Collection">
                  <ValuePillList
                    values={patient.badgeCollection}
                    kind="badge"
                  />
                </RecordFieldRow>
              </RecordDetailCard>
            </ContentSection>
            {available ? (
              <ClientRankLevelRulesSection
                clinicianCode={clinicianCode}
                patientId={patientId}
                onRulesMutated={state.refresh}
                readOnly={workspace.readOnly}
                accessScope={workspace.accessScope}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
